import type React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TopSticky } from "~/components/ui/topSticky";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ErrorBoundary } from "~/errors/errorBoundary";
import { AuthApi } from "~/functions/api/auth";
import BackArrowIcon from "~/images/arrow-left-accent.svg?react";
import reqxignLogoFull from "~/images/reqxign-logo-full.png";
import type { RegisterBody } from "~/interface/auth";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

// 文字制限の定数
const CHARACTER_LIMITS = {
  firstName: 50,
  lastName: 50,
  displayName: 50,
  companyName: 50,
};

interface StepProps {
  nextStep: () => void;
  backStep?: () => void;
}

interface FinishStepProps {
  companyName: string;
  firstName: string;
  lastName: string;
  displayName: string;
  phoneNumber: string;
  backStep: () => void;
  positionSelections: string[];
  onPositionToggle: (optionKey: string) => void;
  currentToolSelections: string[];
  onCurrentToolToggle: (optionKey: string) => void;
  discoverySelections: string[];
  onDiscoveryToggle: (optionKey: string) => void;
  discoveryEventDetail: string;
  discoveryReferralDetail: string;
  discoverySnsDetail: string;
  discoverySearchDetail: string;
  discoveryOtherDetail: string;
  onDiscoveryFreeTextChange: (optionKey: string, value: string) => void;
  motivationSelections: string[];
  onMotivationToggle: (optionKey: string) => void;
  motivationOtherDetail: string;
  onMotivationFreeTextChange: (optionKey: string, value: string) => void;
  motivationInterestedSelections: string[];
  onMotivationInterestedToggle: (optionKey: string) => void;
  motivationIssueSelections: string[];
  onMotivationIssueToggle: (optionKey: string) => void;
  motivationIssueOtherDetail: string;
  onMotivationIssueFreeTextChange: (optionKey: string, value: string) => void;
}

interface OrganizationStepProps extends StepProps {
  companyName: string;
  setCompanyName: React.Dispatch<React.SetStateAction<string>>;
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  backStep: () => void;
}

interface UserSettingStepProps extends StepProps {
  firstName: string;
  setFirstName: React.Dispatch<React.SetStateAction<string>>;
  lastName: string;
  setLastName: React.Dispatch<React.SetStateAction<string>>;
  displayName: string;
  setDisplayName: React.Dispatch<React.SetStateAction<string>>;
  backStep: () => void;
}

const SignUpSettingsModal: React.FC = () => {
  const [companyName, setCompanyName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [step, setStep] = useState(1);
  const [positionSelections, setPositionSelections] = useState<string[]>([]);
  const [currentToolSelections, setCurrentToolSelections] = useState<string[]>([]);
  const [discoverySelections, setDiscoverySelections] = useState<string[]>([]);
  const [motivationSelections, setMotivationSelections] = useState<string[]>([]);
  const [motivationInterestedSelections, setMotivationInterestedSelections] = useState<string[]>(
    [],
  );
  const [motivationIssueSelections, setMotivationIssueSelections] = useState<string[]>([]);

  // フリーテキスト入力用のstate
  const [discoveryEventDetail, setDiscoveryEventDetail] = useState("");
  const [discoveryReferralDetail, setDiscoveryReferralDetail] = useState("");
  const [discoverySnsDetail, setDiscoverySnsDetail] = useState("");
  const [discoverySearchDetail, setDiscoverySearchDetail] = useState("");
  const [discoveryOtherDetail, setDiscoveryOtherDetail] = useState("");
  const [motivationOtherDetail, setMotivationOtherDetail] = useState("");
  const [motivationIssueOtherDetail, setMotivationIssueOtherDetail] = useState("");

  const handleDiscoveryToggle = useCallback((optionKey: string) => {
    setDiscoverySelections((prevSelections) => {
      const alreadySelected = prevSelections.some((selection) => selection === optionKey);
      if (alreadySelected) {
        return prevSelections.filter((selection) => selection !== optionKey);
      }
      return [...prevSelections, optionKey];
    });
  }, []);

  const handlePositionToggle = useCallback((optionKey: string) => {
    setPositionSelections((prevSelections) => {
      const alreadySelected = prevSelections.some((selection) => selection === optionKey);
      if (alreadySelected) {
        return prevSelections.filter((selection) => selection !== optionKey);
      }
      return [...prevSelections, optionKey];
    });
  }, []);

  const handleCurrentToolToggle = useCallback((optionKey: string) => {
    setCurrentToolSelections((prevSelections) => {
      const alreadySelected = prevSelections.some((selection) => selection === optionKey);
      if (alreadySelected) {
        return prevSelections.filter((selection) => selection !== optionKey);
      }
      return [...prevSelections, optionKey];
    });
  }, []);

  const handleDiscoveryFreeTextChange = useCallback((optionKey: string, value: string) => {
    switch (optionKey) {
      case "その他イベント":
        setDiscoveryEventDetail(value);
        break;
      case "紹介":
        setDiscoveryReferralDetail(value);
        break;
      case "SNS（Facebook、X、Instagramなど）":
        setDiscoverySnsDetail(value);
        break;
      case "検索から（Google、Yahoo!など）":
        setDiscoverySearchDetail(value);
        break;
      case "その他":
        setDiscoveryOtherDetail(value);
        break;
    }
  }, []);

  const handleMotivationToggle = useCallback((optionKey: string) => {
    setMotivationSelections((prevSelections) => {
      const alreadySelected = prevSelections.some((selection) => selection === optionKey);
      if (alreadySelected) {
        return prevSelections.filter((selection) => selection !== optionKey);
      }
      return [...prevSelections, optionKey];
    });
  }, []);

  const handleMotivationFreeTextChange = useCallback((_optionKey: string, value: string) => {
    setMotivationOtherDetail(value);
  }, []);

  const handleMotivationInterestedToggle = useCallback((optionKey: string) => {
    setMotivationInterestedSelections((prevSelections) => {
      const alreadySelected = prevSelections.some((selection) => selection === optionKey);
      if (alreadySelected) {
        return prevSelections.filter((selection) => selection !== optionKey);
      }
      return [...prevSelections, optionKey];
    });
  }, []);

  const handleMotivationIssueToggle = useCallback((optionKey: string) => {
    setMotivationIssueSelections((prevSelections) => {
      const alreadySelected = prevSelections.some((selection) => selection === optionKey);
      if (alreadySelected) {
        return prevSelections.filter((selection) => selection !== optionKey);
      }
      return [...prevSelections, optionKey];
    });
  }, []);

  const handleMotivationIssueFreeTextChange = useCallback((_optionKey: string, value: string) => {
    setMotivationIssueOtherDetail(value);
  }, []);

  const nextStep = () => {
    setStep((prevStep) => (prevStep < 4 ? prevStep + 1 : prevStep));
  };

  const backStep = () => {
    setStep((prevStep) => (prevStep > 1 ? prevStep - 1 : prevStep));
  };

  return (
    <div className={styles["overlay"]}>
      <div className={styles["modal"]}>
        <img src={reqxignLogoFull} alt="REQXIGN Logo" />
        <div className={styles["modalBody"]}>
          {step === 1 && <Welcome nextStep={nextStep} />}
          {step === 2 && (
            <UserSettingComponent
              nextStep={nextStep}
              backStep={backStep}
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              displayName={displayName}
              setDisplayName={setDisplayName}
            />
          )}
          {step === 3 && (
            <OrganizationSettingComponent
              nextStep={nextStep}
              backStep={backStep}
              companyName={companyName}
              setCompanyName={setCompanyName}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
            />
          )}
          {step === 4 && (
            <Finish
              companyName={companyName}
              firstName={firstName}
              lastName={lastName}
              displayName={displayName}
              phoneNumber={phoneNumber}
              backStep={backStep}
              positionSelections={positionSelections}
              onPositionToggle={handlePositionToggle}
              currentToolSelections={currentToolSelections}
              onCurrentToolToggle={handleCurrentToolToggle}
              discoverySelections={discoverySelections}
              onDiscoveryToggle={handleDiscoveryToggle}
              discoveryEventDetail={discoveryEventDetail}
              discoveryReferralDetail={discoveryReferralDetail}
              discoverySnsDetail={discoverySnsDetail}
              discoverySearchDetail={discoverySearchDetail}
              discoveryOtherDetail={discoveryOtherDetail}
              onDiscoveryFreeTextChange={handleDiscoveryFreeTextChange}
              motivationSelections={motivationSelections}
              onMotivationToggle={handleMotivationToggle}
              motivationOtherDetail={motivationOtherDetail}
              onMotivationFreeTextChange={handleMotivationFreeTextChange}
              motivationInterestedSelections={motivationInterestedSelections}
              onMotivationInterestedToggle={handleMotivationInterestedToggle}
              motivationIssueSelections={motivationIssueSelections}
              onMotivationIssueToggle={handleMotivationIssueToggle}
              motivationIssueOtherDetail={motivationIssueOtherDetail}
              onMotivationIssueFreeTextChange={handleMotivationIssueFreeTextChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Welcome: React.FC<StepProps> = ({ nextStep }) => {
  return (
    <div>
      <div className={styles["header-container_welcome"]}>
        <h1>REQXIGNへようこそ！</h1>
      </div>
      <div className={styles["form-description_welcome"]}>
        REQXIGNへようこそ！
        <br />
        まずは、サービスをより便利にお使いいただくための設定を行いましょう。
      </div>
      <div className={styles["actions"]}>
        <button
          className={`${styles["save-button"]} ${styles["override-margin"]}`}
          onClick={nextStep}
          data-testid="welcome-next-button"
        >
          次へ
        </button>
      </div>
    </div>
  );
};

const POSITION_OPTIONS: Array<string> = [
  "システム開発（SIer）を行っている",
  "コンサルティングをしている",
  "自社内でシステム開発/運用をしている",
  "システムを外注して開発している",
  "パッケージ製品を利用している",
  "システムは導入していない",
  "その他",
];

const CURRENT_TOOL_OPTIONS: Array<string> = [
  "kintone",
  "Salesforce",
  "MicrosoftPowerPlatform",
  "FileMaker",
  "その他",
];

const DISCOVERY_OPTIONS: Array<string> = [
  "弊社HPをみた",
  "プレスリリースをみた",
  "サイボウズのページ",
  "CybozuDays",
  "その他イベント",
  "セミナー",
  "紹介",
  "SNS（Facebook、X、Instagramなど）",
  "検索から（Google、Yahoo!など）",
  "その他",
];

const MOTIVATION_OPTIONS: Array<string> = [
  "使ってみたい機能があったから",
  "操作が簡単そうだったから",
  "導入事例を見て、自社でも活用できそうだと感じたから",
  "料金形態が自分に合うと思ったから",
  "デザインやUIが魅力的だったから",
  "無料のフリープランがあったから",
  "知人や同僚から勧められたから",
  "自社の課題解決に貢献すると思ったから",
  "最新のAI技術を試してみたい",
  "その他",
];

const MOTIVATION_INTERESTED_OPTIONS: Array<string> = [
  "議事録生成",
  "システム概要書生成",
  "概算工数見積もり/費用対効果",
  "機能要件書/非機能概要書",
  "業務フロー図",
  "プラグイン提案書",
  "アプリ設計書",
  "フィールド設計書",
  "システム構成図",
  "ER図",
  "カスタマイズ仕様書",
  "AIエージェントチャット",
  "リバースエンジニアリング",
];

const MOTIVATION_ISSUE_OPTIONS: Array<string> = [
  "業務効率を改善/向上したい",
  "ドキュメント作成を効率化したい",
  "提案がマンネリ化している",
  "システムをどう設計するべきかわからない",
  "既存システムのブラックボックス化",
  "人材育成",
  "その他（ご記入ください）",
];

type SurveyItemProps = {
  title: string;
  options: string[];
  selections: string[];
  onToggleOption: (optionKey: string) => void;
  freeTextInputs?: Record<string, string>;
  onFreeTextChange?: (optionKey: string, value: string) => void;
  freeTextTriggers?: string[];
};

const Finish: React.FC<FinishStepProps> = ({
  companyName,
  firstName,
  lastName,
  displayName,
  phoneNumber,
  backStep,
  positionSelections,
  onPositionToggle,
  currentToolSelections,
  onCurrentToolToggle,
  discoverySelections,
  onDiscoveryToggle,
  discoveryEventDetail,
  discoveryReferralDetail,
  discoverySnsDetail,
  discoverySearchDetail,
  discoveryOtherDetail,
  onDiscoveryFreeTextChange,
  motivationSelections,
  onMotivationToggle,
  motivationOtherDetail,
  onMotivationFreeTextChange,
  motivationInterestedSelections,
  onMotivationInterestedToggle,
  motivationIssueSelections,
  onMotivationIssueToggle,
  motivationIssueOtherDetail,
  onMotivationIssueFreeTextChange,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  useSelector((state: RootState) => state.auth);
  const authApi = new AuthApi();
  const isDiscoveryValid = useMemo(() => {
    if (discoverySelections.length === 0) return false;
    return discoverySelections.every((selection) => {
      const option = DISCOVERY_OPTIONS.find((item) => item === selection);
      if (!option) {
        return false;
      }
      return true;
    });
  }, [discoverySelections]);

  const isMotivationValid = useMemo(() => {
    if (motivationSelections.length === 0) return false;
    return motivationSelections.every((selection) => {
      const option = MOTIVATION_OPTIONS.find((item) => item === selection);
      if (!option) {
        return false;
      }
      return true;
    });
  }, [motivationSelections]);

  const surveyIsComplete = isDiscoveryValid && isMotivationValid;

  const handleStart = async () => {
    if (!surveyIsComplete) return;

    setError("");
    setIsLoading(true);

    // discoveryの詳細テキストを統合
    const discoveryFreeTexts: string = [
      `紹介: ${discoveryReferralDetail}`,
      `SNS（Facebook、X、Instagramなど）: ${discoverySnsDetail}`,
      `検索から（Google、Yahoo!など）: ${discoverySearchDetail}`,
      `その他: ${discoveryOtherDetail}`,
    ].join("\n");

    const requestBody: RegisterBody = {
      organization_data: {
        name: companyName,
        url: "",
      },
      user_data: {
        display_name: displayName,
        first_name: firstName || "",
        last_name: lastName || "",
      },
      survey: {
        q_position: positionSelections || [],
        q_current_tool: currentToolSelections || [],
        q_discover: discoverySelections || [],
        discover_details_event: discoveryEventDetail || "",
        discover_details_others: discoveryFreeTexts || "",
        q_motivation: motivationSelections || [],
        motivation_details_other: motivationOtherDetail || "",
        q_motivation_interested: motivationInterestedSelections || [],
        q_motivation_issue: motivationIssueSelections || [],
        q_motivation_issue_other_details: motivationIssueOtherDetail || "",
        phone_number: phoneNumber || "",
      },
    };

    try {
      const response = await authApi.signup(requestBody);
      if (response?.ok) {
        // サインアップ完了後はサインインページに移動（URLは保持される）
        navigate(`/signin`);
      } else {
        const errorData = response ? await response.json() : { message: "不明なエラー" };
        const errorMessage =
          errorData.message || ErrorBoundary(response?.status || 0) || "作成に失敗しました。";
        setError(errorMessage);
        toast.error(TOAST_MESSAGES.SIGNUP.CREATE_FAILED(errorMessage));
      }
    } catch (err) {
      console.error("Signup API error:", err);
      const message = err instanceof Error ? err.message : "予期せぬエラーが発生しました。";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className={styles["header-container"]}>
        <div className={styles["header-topRow"]}>
          <button onClick={backStep} className={styles["back-button"]}>
            <BackArrowIcon />
          </button>
          <h1>簡単なアンケートにご協力ください</h1>
        </div>
      </div>
      <p className={styles["survey-introduction"]}>
        サービス向上のため、以下の質問にご回答をお願いいたします。
      </p>
      <div className={styles["survey-section"]}>
        <SurveyItem
          title="貴社内でのシステム開発における最も近い立場をお選びください"
          options={POSITION_OPTIONS}
          selections={positionSelections}
          onToggleOption={onPositionToggle}
        />
        <SurveyItem
          title="現在利用しているノーコード・ローコードシステム（サービス）を教えてください"
          options={CURRENT_TOOL_OPTIONS}
          selections={currentToolSelections}
          onToggleOption={onCurrentToolToggle}
        />
        <SurveyItem
          title="レクシンを何で知りましたか"
          options={DISCOVERY_OPTIONS}
          selections={discoverySelections}
          onToggleOption={onDiscoveryToggle}
          freeTextInputs={{
            その他イベント: discoveryEventDetail,
            紹介: discoveryReferralDetail,
            "SNS（Facebook、X、Instagramなど）": discoverySnsDetail,
            "検索から（Google、Yahoo!など）": discoverySearchDetail,
            その他: discoveryOtherDetail,
          }}
          onFreeTextChange={onDiscoveryFreeTextChange}
          freeTextTriggers={[
            "その他イベント",
            "紹介",
            "SNS（Facebook、X、Instagramなど）",
            "検索から（Google、Yahoo!など）",
            "その他",
          ]}
        />
        <SurveyItem
          title="レクシンをトライアルしようと思ったきっかけは何ですか"
          options={MOTIVATION_OPTIONS}
          selections={motivationSelections}
          onToggleOption={onMotivationToggle}
          freeTextInputs={{
            その他: motivationOtherDetail,
          }}
          onFreeTextChange={onMotivationFreeTextChange}
          freeTextTriggers={["その他"]}
        />
        <SurveyItem
          title="どの機能に興味があるかをお選びください"
          options={MOTIVATION_INTERESTED_OPTIONS}
          selections={motivationInterestedSelections}
          onToggleOption={onMotivationInterestedToggle}
        />
        <SurveyItem
          title="具体的な課題を教えてください"
          options={MOTIVATION_ISSUE_OPTIONS}
          selections={motivationIssueSelections}
          onToggleOption={onMotivationIssueToggle}
          freeTextInputs={{
            "その他（ご記入ください）": motivationIssueOtherDetail,
          }}
          onFreeTextChange={onMotivationIssueFreeTextChange}
          freeTextTriggers={["その他（ご記入ください）"]}
        />
        {/* きっかけ課題・その他 */}
      </div>
      {error && <p className={styles["error-message"]}>{error}</p>}
      <div className={styles["actions"]}>
        <button
          className={`${styles["save-button"]} ${styles["override-margin"]}`}
          onClick={handleStart}
          disabled={!surveyIsComplete || isLoading}
        >
          {isLoading ? "作成中..." : "回答してREQXIGNを始める"}
        </button>
      </div>
    </div>
  );
};

const SurveyItem: React.FC<SurveyItemProps> = ({
  title,
  options,
  selections,
  onToggleOption,
  freeTextInputs = {},
  onFreeTextChange,
  freeTextTriggers = [],
}) => {
  return (
    <div className={styles["survey-item"]}>
      <h2>{title}</h2>
      <div className={styles["survey-options"]}>
        {options.map((option) => {
          const selected = selections.find((selection) => selection === option);
          const isSelected = Boolean(selected);
          const shouldShowFreeText = freeTextTriggers.includes(option) && isSelected;

          return (
            <div key={`${title}-${option}`} className={styles["survey-option"]}>
              <label className={styles["survey-checkboxLabel"]}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleOption(option)}
                />
                <span>{option}</span>
              </label>
              {shouldShowFreeText && onFreeTextChange && (
                <textarea
                  className={styles["survey-freeInputTextarea"]}
                  value={freeTextInputs[option] || ""}
                  onChange={(e) => onFreeTextChange(option, e.target.value)}
                  placeholder="詳細を入力してください"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UserSettingComponent: React.FC<UserSettingStepProps> = ({
  nextStep,
  backStep,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  displayName,
  setDisplayName,
}) => {
  const [lastNameError, setLastNameError] = useState<string>("");
  const [firstNameError, setFirstNameError] = useState<string>("");
  const [displayNameError, setDisplayNameError] = useState<string>("");

  // 文字数チェック
  const lastNameCount = useMemo(() => lastName.length, [lastName]);
  const firstNameCount = useMemo(() => firstName.length, [firstName]);
  const displayNameCount = useMemo(() => displayName.length, [displayName]);

  // 文字数制限を超えているかのチェック
  const hasExceededLimits = useMemo(() => {
    return (
      lastNameCount > CHARACTER_LIMITS.lastName ||
      firstNameCount > CHARACTER_LIMITS.firstName ||
      displayNameCount > CHARACTER_LIMITS.displayName
    );
  }, [lastNameCount, firstNameCount, displayNameCount]);

  const handleNextStep = () => {
    let hasError = false;

    // 姓のバリデーション
    if (!lastName) {
      setLastNameError("姓を入力してください");
      hasError = true;
    } else if (lastNameCount > CHARACTER_LIMITS.lastName) {
      setLastNameError("入力された文字数が制限を超えています");
      hasError = true;
    } else {
      setLastNameError("");
    }

    // 名のバリデーション
    if (!firstName) {
      setFirstNameError("名を入力してください");
      hasError = true;
    } else if (firstNameCount > CHARACTER_LIMITS.firstName) {
      setFirstNameError("入力された文字数が制限を超えています");
      hasError = true;
    } else {
      setFirstNameError("");
    }

    // 表示名のバリデーション
    if (!displayName) {
      setDisplayNameError("表示名を入力してください");
      hasError = true;
    } else if (displayNameCount > CHARACTER_LIMITS.displayName) {
      setDisplayNameError("入力された文字数が制限を超えています");
      hasError = true;
    } else {
      setDisplayNameError("");
    }

    if (hasError) {
      return;
    }

    nextStep();
  };

  return (
    <div>
      <div className={styles["header-container"]}>
        <div className={styles["header-secondaryRow"]}>
          <TopSticky content="ステップ1 / 2" />
        </div>
        <div className={styles["header-topRow"]}>
          <button onClick={backStep} className={styles["back-button"]}>
            <BackArrowIcon />
          </button>
          <h1>ユーザー情報の設定</h1>
        </div>
      </div>
      <div className={styles["name-container"]}>
        <div className={`${styles["form-group"]}`}>
          <label className={styles["label-with-badge"]}>
            <span className={styles["label-text"]}>姓</span>
            <span className={styles["required-badge"]}>必須</span>
            <span
              className={`${styles["char-counter"]} ${lastNameCount > CHARACTER_LIMITS.lastName ? styles["exceeded"] : ""}`}
            >
              {lastNameCount}/{CHARACTER_LIMITS.lastName}
            </span>
          </label>
          <input
            type="text"
            value={lastName}
            placeholder="ノベル"
            onChange={(e) => {
              setLastName(e.target.value);
              if (lastNameError) {
                setLastNameError("");
              }
            }}
            maxLength={CHARACTER_LIMITS.lastName}
            data-testid="signup-lastname"
            required
          />
          {lastNameError && (
            <p className={`${styles["error-message"]} ${styles["error-message--spaced"]}`}>
              {lastNameError}
            </p>
          )}
        </div>
        <div className={`${styles["form-group"]}`}>
          <label className={styles["label-with-badge"]}>
            <span className={styles["label-text"]}>名</span>
            <span className={styles["required-badge"]}>必須</span>
            <span
              className={`${styles["char-counter"]} ${firstNameCount > CHARACTER_LIMITS.firstName ? styles["exceeded"] : ""}`}
            >
              {firstNameCount}/{CHARACTER_LIMITS.firstName}
            </span>
          </label>
          <input
            type="text"
            value={firstName}
            placeholder="筋太郎"
            onChange={(e) => {
              setFirstName(e.target.value);
              if (firstNameError) {
                setFirstNameError("");
              }
            }}
            maxLength={CHARACTER_LIMITS.firstName}
            data-testid="signup-firstname"
            required
          />
          {firstNameError && (
            <p className={`${styles["error-message"]} ${styles["error-message--spaced"]}`}>
              {firstNameError}
            </p>
          )}
        </div>
      </div>
      <div className={styles["form-group"]}>
        <label className={styles["label-with-badge"]}>
          <span className={styles["label-text"]}>表示名</span>
          <span className={styles["required-badge"]}>必須</span>
          <span
            className={`${styles["char-counter"]} ${displayNameCount > CHARACTER_LIMITS.displayName ? styles["exceeded"] : ""}`}
          >
            {displayNameCount}/{CHARACTER_LIMITS.displayName}
          </span>
        </label>
        <input
          type="text"
          value={displayName}
          placeholder="表示名"
          onChange={(e) => {
            setDisplayName(e.target.value);
            if (displayNameError) {
              setDisplayNameError("");
            }
          }}
          maxLength={CHARACTER_LIMITS.displayName}
          required
          data-testid="signup-displayname"
        />
        {displayNameError && (
          <p className={`${styles["error-message"]} ${styles["error-message--spaced"]}`}>
            {displayNameError}
          </p>
        )}
      </div>
      <div>
        <button
          className={`${styles["save-button"]} ${styles["override-margin"]}`}
          onClick={handleNextStep}
          disabled={hasExceededLimits}
          data-testid="signup-user-next"
        >
          次へ
        </button>
      </div>
    </div>
  );
};

const OrganizationSettingComponent: React.FC<OrganizationStepProps> = ({
  nextStep,
  backStep,
  companyName,
  setCompanyName,
  phoneNumber,
  setPhoneNumber,
}) => {
  const [companyNameError, setCompanyNameError] = useState<string>("");
  const [phoneNumberError, setPhoneNumberError] = useState<string>("");
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);

  // 文字数チェック
  const companyNameCount = useMemo(() => companyName.length, [companyName]);

  // 文字数制限を超えているかのチェック
  const hasExceededLimits = useMemo(() => {
    return companyNameCount > CHARACTER_LIMITS.companyName;
  }, [companyNameCount]);

  // 電話番号入力ハンドラー（数字のみを許可）
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 数字のみを抽出（0-9のみ）
    // maxLength={11}により、ブラウザが11文字を超える入力を自動的に制限する
    const numericValue = value.replace(/[^0-9]/g, "");

    setPhoneNumber(numericValue);

    // エラーがある場合はクリア
    if (phoneNumberError) {
      setPhoneNumberError("");
    }
  };

  const handleNext = () => {
    let hasError = false;

    // 組織名のバリデーション
    if (!companyName) {
      setCompanyNameError("組織名を入力してください");
      hasError = true;
    } else if (hasExceededLimits) {
      setCompanyNameError("入力された文字数が制限を超えています");
      hasError = true;
    } else {
      setCompanyNameError("");
    }

    // 電話番号のバリデーション（pattern属性を使用）
    if (!phoneNumber) {
      setPhoneNumberError("電話番号を入力してください");
      hasError = true;
    } else if (phoneNumber.length < 10 || phoneNumber.length > 11) {
      setPhoneNumberError("電話番号は10桁または11桁で入力してください");
      hasError = true;
    } else if (phoneNumberInputRef.current && !phoneNumberInputRef.current.checkValidity()) {
      // pattern属性によるバリデーション結果をチェック
      setPhoneNumberError(
        "電話番号は0から始まるハイフンなし10桁または11桁の数字で入力してください",
      );
      hasError = true;
    } else {
      setPhoneNumberError("");
    }

    if (hasError) {
      return;
    }

    nextStep();
  };

  return (
    <div>
      <div className={styles["header-container"]}>
        <div className={styles["header-secondaryRow"]}>
          <TopSticky content="ステップ2 / 2" />
        </div>
        <div className={styles["header-topRow"]}>
          <button onClick={backStep} className={styles["back-button"]}>
            <BackArrowIcon />
          </button>
          <h1>組織の設定</h1>
        </div>
      </div>
      <div className={styles["organization-form-wrapper"]}>
        <div className={styles["form-group"]}>
          <label className={styles["label-with-badge"]}>
            <span className={styles["label-text"]}>組織名</span>
            <span className={styles["required-badge"]}>必須</span>
            <span
              className={`${styles["char-counter"]} ${companyNameCount > CHARACTER_LIMITS.companyName ? styles["exceeded"] : ""}`}
            >
              {companyNameCount}/{CHARACTER_LIMITS.companyName}
            </span>
          </label>
          <input
            type="text"
            value={companyName}
            placeholder="株式会社ノベルワークス"
            onChange={(e) => {
              setCompanyName(e.target.value);
              if (companyNameError) {
                setCompanyNameError("");
              }
            }}
            maxLength={CHARACTER_LIMITS.companyName}
            required
            data-testid="signup-org-name"
          />
          {companyNameError && (
            <p className={`${styles["error-message"]} ${styles["error-message--spaced"]}`}>
              {companyNameError}
            </p>
          )}
        </div>
        <div className={styles["form-group"]}>
          <label className={styles["label-with-badge"]}>
            <span className={styles["label-text"]}>電話番号</span>
            <span className={styles["required-badge"]}>必須</span>
            <span className={styles["char-counter"]}></span>
          </label>
          <input
            ref={phoneNumberInputRef}
            type="tel"
            value={phoneNumber}
            placeholder="0312345678"
            onChange={handlePhoneNumberChange}
            maxLength={11}
            required
            pattern="0\d{9,10}"
          />
          {phoneNumberError && (
            <p className={`${styles["error-message"]} ${styles["error-message--spaced"]}`}>
              {phoneNumberError}
            </p>
          )}
          {companyNameError && (
            <p className={`${styles["error-message"]} ${styles["error-message--spaced"]}`}>
              {companyNameError}
            </p>
          )}
        </div>
        <div style={{ marginTop: "20px" }}></div>
      </div>
      <div className={styles["actions"]}>
        <button
          className={styles["save-button"]}
          onClick={handleNext}
          disabled={hasExceededLimits}
          data-testid="signup-org-next"
        >
          次へ
        </button>
      </div>
    </div>
  );
};

export default SignUpSettingsModal;
